import React, { useState } from 'react';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { CardButton } from '../components/design-system/CardButton';
import { Navigation } from '../components/Navigation';
import { FooterContent } from 'components';
import {
  walletTemplates,
  categoryColors,
  WalletTemplate,
} from 'data/shared/walletTemplates';
import { CategoryEarnRatePill } from '../components/CreditPills';
import { flattenRewardsEarnRates } from 'data/shared/utils/flattenRewardsEarnRates';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { SegmentedControl } from '../components/design-system/SegmentedControl';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Gets the top earn rate pills for a wallet template
 */
function getWalletEarnRatePills(wallet: WalletTemplate): Array<{
  category: string;
  earnRate: number;
  rewardsType: CreditCardRewardsType;
}> {
  const rateMap = new Map<
    string,
    { rate: number; rewardsType: CreditCardRewardsType }
  >();

  wallet.cards.forEach((cardId) => {
    const cardData = CreditCardDataFiltered[cardId];
    if (!cardData?.rewards_earn_rates) {
      return;
    }

    // Filter out rates that require travel portal (consistent with table logic)
    const filteredRates = cardData.rewards_earn_rates.filter(
      (rate) => !rate.requiresTravelPortal
    );

    // Flatten earn rates to handle selectable categories, quarterly categories, etc.
    const flattenedRates = flattenRewardsEarnRates(
      filteredRates,
      undefined, // No user selections for wallet templates
      cardData.rewardsType
    );

    flattenedRates.forEach((rate) => {
      if (!rate.category || !rate.earnRate) {
        return;
      }

      const current = rateMap.get(rate.category);
      // Take the maximum rate for each category
      if (!current || rate.earnRate > current.rate) {
        rateMap.set(rate.category, {
          rate: rate.earnRate,
          rewardsType: (cardData.rewardsType ||
            'cash_back') as CreditCardRewardsType,
        });
      }
    });
  });

  // Get category depth for sorting
  const getCategoryDepth = (category: string): number => {
    return category.split('.').length;
  };

  // Separate allPurchases from other categories
  const allPurchasesCategory = Array.from(rateMap.entries()).find(
    ([category]) => category === 'allPurchases' || category === 'all_purchases'
  );

  const otherCategories = Array.from(rateMap.entries())
    .filter(
      ([category]) =>
        category !== 'allPurchases' && category !== 'all_purchases'
    )
    .map(([category, { rate, rewardsType }]) => ({
      category,
      earnRate: rate,
      rewardsType,
      depth: getCategoryDepth(category),
    }))
    .filter((item) => item.earnRate > 1) // Only show rates > 1%
    .sort((a, b) => {
      // Prioritize categories with depth 1 or 2
      const depthA = a.depth <= 2 ? 1 : 0;
      const depthB = b.depth <= 2 ? 1 : 0;

      // If one is shallow (1-2 levels) and the other is not, prioritize the shallow one
      if (depthA !== depthB) {
        return depthB - depthA;
      }

      // Otherwise, sort by rate
      return b.earnRate - a.earnRate;
    })
    .slice(0, 4) // Show top 4 earn rates
    .map(({ category, earnRate, rewardsType }) => ({
      category,
      earnRate,
      rewardsType,
    }));

  // Always include allPurchases if it exists, even if it's 1%
  const result: Array<{
    category: string;
    earnRate: number;
    rewardsType: CreditCardRewardsType;
  }> = [];

  if (allPurchasesCategory) {
    const [, { rate, rewardsType }] = allPurchasesCategory;
    result.push({
      category: allPurchasesCategory[0],
      earnRate: rate,
      rewardsType: (rewardsType || 'cash_back') as CreditCardRewardsType,
    });
  }

  // Add other categories
  result.push(...otherCategories);

  return result;
}

const BrowseWallets = () => {
  usePageMeta({
    title: 'Browse Credit Card Wallets - CardClues',
    description:
      'Explore pre-built credit card wallet combinations optimized for travel, cashback, dining, and more. Find the perfect card combo for your spending.',
    canonicalPath: '/browse-wallets',
  });

  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  const filteredWallets = walletTemplates.filter((wallet) => {
    // Always show the "start from scratch" card
    if (wallet.id === 'start-from-scratch') {
      return true;
    }
    if (selectedFilter === 'all') {
      return true;
    }
    if (selectedFilter === 'cashback') {
      return wallet.category === 'cashback';
    }
    if (selectedFilter === 'travel') {
      return wallet.category === 'travel';
    }
    if (selectedFilter === 'business') {
      return wallet.category === 'business';
    }
    return true;
  });

  // Sort so "start from scratch" appears last
  const sortedWallets = [...filteredWallets].sort((a, b) => {
    if (a.id === 'start-from-scratch') {
      return 1;
    }
    if (b.id === 'start-from-scratch') {
      return -1;
    }
    return 0;
  });

  return (
    <>
      <Navigation />
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <h1
          style={{
            fontFamily: 'Merriweather',
            fontSize: '32px',
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          Powerful Credit Card Combos for Different Goals
        </h1>

        <p
          style={{
            textAlign: 'center',
            marginBottom: '40px',
            color: '#666',
            fontSize: '16px',
            maxWidth: '800px',
            margin: '0 auto 40px auto',
          }}
        >
          Explore U.S.-based credit card combinations tailored to various
          financial goals. Click any wallet to load it and see how the cards
          work together or customize it to your needs.
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <SegmentedControl
            options={[
              { value: 'all', label: 'All' },
              { value: 'cashback', label: 'Cashback' },
              { value: 'travel', label: 'Travel' },
              { value: 'business', label: 'Business' },
            ]}
            value={selectedFilter}
            onChange={setSelectedFilter}
          />
          <button
            onClick={() => {
              window.location.href = '/';
            }}
            style={{
              textDecoration: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#e5e7eb',
              color: '#1f2937',
              fontWeight: 500,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
          >
            Start with a blank wallet
          </button>
        </div>

        {/* Wallets List */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '40px',
          }}
        >
          {sortedWallets.map((wallet, index) => {
            const isStartFromScratch = wallet.id === 'start-from-scratch';

            return (
              <a
                key={index}
                href={isStartFromScratch ? '/' : `/w/${wallet.id}`}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'white',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Category Badge */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '12px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      color: categoryColors[wallet.category],
                      backgroundColor: `${categoryColors[wallet.category]}15`,
                      padding: '4px 12px',
                      borderRadius: '12px',
                    }}
                  >
                    {wallet.category}
                  </span>
                </div>

                {/* Wallet Name */}
                <h3
                  style={{
                    fontFamily: 'Merriweather',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    margin: '0 0 8px 0',
                    color: '#1a1a1a',
                  }}
                >
                  {wallet.name}
                </h3>

                {/* Wallet Description */}
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.5',
                    margin: '0 0 12px 0',
                  }}
                >
                  {wallet.editorsNote}
                </p>

                {/* Wallet Details */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    marginBottom: '16px',
                    fontSize: '13px',
                  }}
                >
                  {/* <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: '#999', fontWeight: 500 }}>
                      Complexity:
                    </span>
                    <span style={{ color: '#333' }}>
                      {isStartFromScratch ? (
                        '?'
                      ) : (
                        <>
                          {'⭐'.repeat(wallet.complexity)}{' '}
                          {wallet.complexity === 1 && '(Very Easy)'}
                          {wallet.complexity === 2 && '(Easy)'}
                          {wallet.complexity === 3 && '(Moderate)'}
                          {wallet.complexity === 4 && '(Advanced)'}
                          {wallet.complexity === 5 && '(Expert)'}
                        </>
                      )}
                    </span>
                  </div> */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: '#999', fontWeight: 500 }}>
                      Total Annual Fees:
                    </span>
                    <span style={{ color: '#333' }}>
                      {isStartFromScratch ? '?' : wallet.annualFees}
                    </span>
                  </div>
                </div>

                {/* Earn Rate Pills */}
                {!isStartFromScratch &&
                  (() => {
                    const earnRatePills = getWalletEarnRatePills(wallet);
                    if (earnRatePills.length === 0) {
                      return null;
                    }
                    return (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          marginBottom: '16px',
                        }}
                      >
                        {earnRatePills.map((pill, index) => (
                          <CategoryEarnRatePill
                            key={`${pill.category}-${index}`}
                            category={pill.category}
                            earnRate={pill.earnRate}
                            rewardsType={pill.rewardsType}
                          />
                        ))}
                      </div>
                    );
                  })()}

                {/* Cards in Wallet */}
                {!isStartFromScratch && wallet.cards.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {wallet.cards
                      .filter((cardId) => CreditCardDataFiltered[cardId])
                      .map((cardId) => {
                        const cardData = CreditCardDataFiltered[cardId];
                        if (!cardData) {
                          return null;
                        }

                        return (
                          <div
                            key={cardId}
                            style={{ marginBottom: '4px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <CardButton
                              cardData={cardData}
                              size="small"
                              hideName={false}
                            />
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* Empty card placeholders for start from scratch */}
                {isStartFromScratch && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {[1, 2, 3].map((placeholderIndex) => (
                      <div
                        key={placeholderIndex}
                        style={{
                          width: 96,
                          height: Math.round(96 / 1.4), // Match fallback aspect ratio
                          backgroundColor: 'rgb(237, 239, 240)',
                          border: '2px dashed rgb(164, 182, 195)',
                          borderRadius: '8px',
                          boxSizing: 'border-box',
                          marginBottom: '4px',
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Click indicator */}
                <div
                  style={{
                    marginTop: '16px',
                    fontSize: '12px',
                    color: '#0E80D5',
                    textAlign: 'center',
                    fontWeight: '500',
                  }}
                >
                  {isStartFromScratch
                    ? 'Click to start building →'
                    : 'Click to load wallet →'}
                </div>
              </a>
            );
          })}
        </div>
      </div>
      <FooterContent />
    </>
  );
};

export default BrowseWallets;
